import {last} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf, intersperseNodes} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useListAtomGroups} from '../../../editors/expressions/expressionAtomHelpers';
import {ClientAtom} from '../../../editors/expressions/expressionAtomModel';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {ExpressionDependencies} from '../../../editors/expressions/expressionState';
import {RuleExpressionAddBlock} from '../ruleExpressionAddBlock';
import {AtomCard} from './atomCard';
import {RuleExpressionAtomSeparator} from './ruleExpressionAtomSeparator';

/*
 * Types.
 */

export interface AtomsListProps {
  atoms: ReadonlyArray<ClientAtom>;
  expressionDependencies: ExpressionDependencies;

  isReadOnly?: boolean;
  onAddAtom?: (atom: ClientAtom, functionAtomClientId?: string) => void;
  onUpdateAtom: HandlerOf<ClientAtom>;
  onDeleteAtom?: HandlerOf<ClientAtom>;
  isNested?: boolean;
}

/*
 * Styles.
 */

const StyledAtomsList = styled.div`
  width: 100%;

  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 5px;
  justify-items: center;
`;

/*
 * Component.
 */

export const AtomsList: FC<AtomsListProps> = (props) => {
  const {atoms, onAddAtom} = props;
  const lastAtom = last(atoms);
  const expressionsContext = useExpressionsContext();
  const groups = useListAtomGroups(lastAtom, expressionsContext, props.expressionDependencies);

  if (!lastAtom) {
    return null;
  }

  return (
    <StyledAtomsList>
      {intersperseNodes(
        atoms.map((atom, index) => ({
          node: renderAtom(atom, index === 0, index === atoms.length - 1, props),
          key: atom.clientId,
        })),
        () => !props.isNested && <RuleExpressionAtomSeparator />,
      )}
      {onAddAtom && <RuleExpressionAddBlock groups={groups} onAddAtom={(item) => onAddAtom(item.atom)} />}
    </StyledAtomsList>
  );
};

/*
 * Helpers.
 */

function renderAtom(atom: ClientAtom, isFirst: boolean, isLast: boolean, props: AtomsListProps) {
  const {isReadOnly, onUpdateAtom, onAddAtom, expressionDependencies} = props;
  const onAddAtomHandler =
    isLast && onAddAtom ? (subAtom: ClientAtom) => onAddAtom(subAtom, atom.clientId) : undefined;
  const onDeleteAtom = isLast ? props.onDeleteAtom : undefined;
  const onCancel = !isFirst && onDeleteAtom ? () => onDeleteAtom(atom) : undefined;

  return (
    <AtomCard
      atom={atom}
      expressionDependencies={expressionDependencies}
      isDisabled={isReadOnly}
      onAddAtom={onAddAtomHandler}
      onUpdateAtom={onUpdateAtom}
      onDeleteAtom={onDeleteAtom}
      onCancel={onCancel}
    />
  );
}
